
import type { PiniaPluginContext }
  from 'pinia';
import ThermalPrinterJob from "~/services/thermalPrinter/ThermalPrinterJob";
import ThermalPrinterBase from '~/services/thermalPrinter/ThermalPrinterBase';
import { usePosShopStore } from '~/stores/pos.shop';
import ThermalPrintReceipt from '~/class/printer/ThermalPrintReceipt';
import ThermalPrintSummaryIncome from '~/class/printer/ThermalPrintSummaryIncome';
import ThermalPrintProductSold from '~/class/printer/ThermalPrintProductSold';
import ThermalPrintVoidReport from '~/class/printer/ThermalPrintVoidReport';
import ThermalPrintOpenMenuReport from '~/class/printer/ThermalPrintOpenMenuReport';
import ThermalPrintVoidReceipt from '~/class/printer/ThermalPrintVoidReceipt';
import { SettingType } from '~/interface/Setting';
import dayjs from 'dayjs';
import { detectPrinterVersion, isPrinterV3 } from '~/utils/printerVersion';
import printJob from '~/utils/printers/printJobToKitchen';
import { claimJobPrint, sweepStalePrintMarkers } from '~/utils/printers/printOnce';




export default defineNuxtPlugin(({ $pinia }) => {

  detectPrinterVersion()

  sweepStalePrintMarkers()

  const jobV1Store = usePosJobsStore()
  jobV1Store.$onAction(({
    name, // name of the action
    store, // store instance, same as `someStore`
    args, // array of parameters passed to the action
    after, // hook after the action returns or resolves
    onError, // hook if the action throws or rejects
  }) => {
    if (name == "add") {
      const job = args[0]
      const isSync = args[1]
      console.log("job added", job)
      after((returnPayload) => {
        if (!claimJobPrint(job?.jobId)) {
          console.log('kitchen print skipped — jobId already printed', job?.jobId)
          return
        }
        printJob(job)
      })
    }
  })


  const posPrintPreviewStore = usePosPrintPreviewStore()
  posPrintPreviewStore.$onAction(({
    name,
    store,
    after,
  }) => {
    if (name == "printPreviewByTable") {
      after((receipt) => {
        if (!receipt) {
          return
        }
        const settingStore = useSettingsStore()

        printPreviewReceipt(store, receipt, settingStore)
      })
    }
  })

  const paymentStore = usePosPaymentStore()
  paymentStore.$onAction(({
    name, // name of the action
    store, // store instance, same as `someStore`
    args, // array of parameters passed to the action
    after, // hook after the action returns or resolves
    onError, // hook if the action throws or rejects
  }) => {
    console.log("receipt plugin=>", name)

    if (name == "completePayment" || name == "completeMultiplePayment") {
      after((receipt) => {
        const settingStore = useSettingsStore()

        receipt && printReceipt(store, receipt, settingStore)
      })
    }
  })


  const receiptsStore = usePosReceiptsStore()
  receiptsStore.$onAction(({ name, args, after }) => {
    if (name !== 'remove') return
    const receipt = args[0]
    const reason = args[1]
    after(() => {
      
      printVoidedReceipt(receipt, reason)
    })
  })


  const tableInstanceStore = usePosTableInstanceStore()
  tableInstanceStore.$onAction(({
    name, // name of the action
    store, // store instance, same as `someStore`
    args, // array of parameters passed to the action
    after, // hook after the action returns or resolves
    onError, // hook if the action throws or rejects
  }) => {
    if (name == "printPreview") {
      after((receipt) => {
        const settingStore = useSettingsStore()
        const shopStore = usePosShopStore()

        // Terminal + Remote Print: the Main Cashier is the only printer — send the
        // request there and do NOT print locally (this terminal has no printer).
        if (shopStore.getRunMode == 'terminal' && settingStore.printPreviewRemote) {
          const posTerminalStore = usePosTerminalStore()
          posTerminalStore.sendPrintPreviewRequest(tableInstanceStore.selectedTableId, receipt)
          return
        }

        // Default terminal (own printer) + cashier + 2nd cashier: print locally.
        printPreviewReceipt(store, receipt, settingStore)
      })
    }

    if (name == "printShiftTime") {
      after((payload) => {
        printShiftTime(payload)

      })
    }
  })


  const reportStore = useReportStore()
  reportStore.$onAction((action) => {
    const reports = ['printSummary', 'printProductSold', 'printVoidItems', 'printOpenMenu']
    if (reports.includes(action.name)) {
      action.after((payload) => {
        printReport(action.name, payload)

      })
    }
  })


  // const logsStore = useLogsStore()
  // logsStore.$onAction((action) => {
  //   const reports = ['rePrintJobToKitchen']
  //   if (reports.includes(action.name)) {
  //     action.after((receipt) => {
  //       rePrintJobToKitchen(receipt)

  //     })
  //   }
  // })
})



//FUNCTIONS
function printReceipt(store: any, receipt: any, settingStore: any) {
  const isCopy = store.isCopyReceipt
  const paymentPath = isPrinterV3() ? "/ReceiptV2" : "/printPayment"
   
  const shouldOpenDrawer = shouldOpenCashDrawer(store, receipt)
  setTimeout(async () => {
    if (shouldOpenDrawer) {
      new ThermalPrinterBase("/openDrawer").openDrawer()
    }
    const printReceipt = new ThermalPrintReceipt(paymentPath).printReceipt(receipt, {
      enableCustomerCount: settingStore.enableCustomerMode,
      isPrinterInterfaceV3: isPrinterV3(),
    });
    printReceipt.print();
  }, 10);

  if (!isCopy) {
    return
  }

  // 50ms (not 10) so the copy never lands in the same ms as the original —
  // second line of defense next to the _C id suffix in the builder.
  setTimeout(() => {
    const printReceipt = new ThermalPrintReceipt(paymentPath).printReceipt(receipt, {
      isCopy: true,
      isPrinterInterfaceV3: isPrinterV3(),
      enableCustomerCount: settingStore.enableCustomerMode

    });
    printReceipt.print();
  }, 100);
}

// Legacy behaviour: the cash drawer kicks whenever a payment is taken that is
// NOT a credit card (cash, bank transfer, QR/PromptPay, etc.). A credit-card
// payment never opens the drawer. The `isCreditCard` flag lives on each
// configured payment type (see PaymentOption in stores/pos.payment.ts), so we
// resolve the receipt's payment name(s) back to their option to read the flag.
function shouldOpenCashDrawer(store: any, receipt: any): boolean {
  const options = store?.paymentOptions || []

  // A payment name is a credit card only if its configured option says so.
  // Unknown / unmatched names default to NON-credit-card (drawer opens) — this
  // preserves the old fallback of kicking the drawer for legacy receipts.
  const isCreditCardByName = (value: any): boolean => {
    const key = String(value || '').trim()
    if (!key) return false
    const opt = options.find((o: any) => o.name === key || o.uuid === key)
    return !!opt?.isCreditCard
  }

  const payments = receipt?.multiplePayment
  if (Array.isArray(payments) && payments.length > 0) {
    // Split payment: kick the drawer if ANY line is not a credit card.
    return payments.some(
      (p: any) => !isCreditCardByName(p?.name || p?.type || p?.paymentId)
    )
  }

  // Single payment: `multiplePayment` / `PaymentType` is just the payment name.
  const name = (typeof payments === 'string' && payments) || receipt?.PaymentType
  return !isCreditCardByName(name)
}

function printPreviewReceipt(store: any, receipt: any, settingStore: any) {
   
  const previewPath = isPrinterV3() ? "/PreviewV2" : "/printPreview"
  setTimeout(() => {
    const printPreviewReceipt = new ThermalPrintReceipt(previewPath).printReceipt(receipt, {
      isPreview: true,
      enableCustomerCount: settingStore.enableCustomerMode,
      isPrinterInterfaceV3: isPrinterV3()
    });
    printPreviewReceipt.print();
  }, 10);
}

function printShiftTime(payload: any) {
  setTimeout(async () => {
    const printReceipt = await new ThermalPrinterJob("/printcustom").printShift(payload);
    printReceipt.print();
  }, 10);
}

function resolvePaymentType(receipt: any): string {
  const multi = receipt?.multiplePayment
  if (Array.isArray(multi) && multi.length > 0) {
    return multi
      .map((p: any) => p?.name || p?.type || p?.paymentId)
      .filter(Boolean)
      .join(', ') || '-'
  }
  return receipt?.PaymentType || receipt?.paymentType || '-'
}

function printVoidedReceipt(receipt: any, reason: string) {
  if (!receipt) return
  const employeeName = usePosEmployeesStore().getCurrentEmployee?.name || 'unknown'
  const payload = {
    invoiceId: receipt?.ReceiptId || receipt?.receiptId || receipt?.OrderId || '-',
    dateTime: receipt?.Date || receipt?.OrderDate || receipt?.createdAt || '-',
    table: receipt?.Table || receipt?.table || '-',
    grandTotal: Number(receipt?.GrandTotal ?? receipt?.grandTotal ?? 0),
    paymentType: resolvePaymentType(receipt),
    voidedAt: dayjs().toISOString(),
    employee: employeeName,
    reason: reason || 'voided',
  }
  setTimeout(() => {
    new ThermalPrintVoidReceipt('/printcustom').printVoidReceipt(payload).print()
  }, 10)
}

function printReport(actionName: string, payload: any) {

  switch (actionName) {
    case 'printSummary':
      new ThermalPrintSummaryIncome("/printcustom").printSummaryIncome(payload).print()
      break;
    case 'printProductSold':
      new ThermalPrintProductSold("/printcustom").printProductSold(payload).print()
      break;
    case 'printVoidItems':
      new ThermalPrintVoidReport("/printcustom").printVoid(payload).print()
      break;
    case 'printOpenMenu':
      new ThermalPrintOpenMenuReport("/printcustom").printOpenMenu(payload).print()
      break;
  }
}
